import {
  type Chain,
  type DynamicClient,
  UserRejectedError,
  type WalletProviderMetadata,
  assertWalletAccountSigningAvailability,
  getWalletAccountAddressByType,
} from '@dynamic-labs-sdk/client';
import {
  DYNAMIC_ICONIC_SPRITE_URL,
  assertDefined,
  createWalletProviderEventEmitter,
  formatWalletProviderGroupKey,
  formatWalletProviderKey,
  getActiveNetworkIdFromLastKnownRegistry,
} from '@dynamic-labs-sdk/client/core';
import { WalletProviderEnum } from '@dynamic-labs/sdk-api-core';
import { Psbt } from 'bitcoinjs-lib';
import { type Json, createUnsecuredToken } from 'jsontokens';
import type {
  BitcoinProvider,
  GetAddressResponse,
  SignMultiplePsbtPayload,
  SignMultipleTransactionsPayload,
} from 'sats-connect';
import {
  AddressPurpose,
  addListener,
  getAddress,
  signMessage as satsConnectSignMessage,
  sendBtcTransaction,
  signTransaction,
} from 'sats-connect';

import type {
  BitcoinSendTransactionResponse,
  BitcoinWalletProvider,
} from '../../../../BitcoinWalletProvider.types';
import { InvalidPsbtError } from '../../../../errors/InvalidPsbtError';
import { bitcoinExecuteCheckoutTransaction } from '../../../../utils/bitcoinExecuteCheckoutTransaction';
import { bitcoinTransferAmount } from '../../../../utils/bitcoinTransferAmount';
import { convertNetworkIdForPsbt } from '../../../../utils/convertNetworkIdForPsbt';
import { convertNetworkIdToSatsConnectNetworkType } from '../../../../utils/convertNetworkIdToSatsConnectNetworkType';
import { getPsbtInputsToSignForSatsConnect } from '../../../../utils/getPsbtInputsToSignForSatsConnect';
import { getSatsConnectSigningProtocol } from '../../../../utils/getSatsConnectSigningProtocol';
import { parseBitcoinConnectionResult } from '../../../../utils/parseBitcoinConnectionResult';

export const MAGIC_EDEN_METADATA: WalletProviderMetadata = {
  displayName: 'Magic Eden',
  icon: `${DYNAMIC_ICONIC_SPRITE_URL}#magiceden`,
  supportedHardwareWalletVendors: ['ledger'],
};

type CreateMagicEdenBitcoinWalletProviderParams = {
  dynamicClient: DynamicClient;
  satsConnectProvider: BitcoinProvider;
};

// https://docs-wallet.magiceden.io/bitcoin/detecting-the-provider
export const createMagicEdenBitcoinWalletProvider = ({
  dynamicClient,
  satsConnectProvider,
}: CreateMagicEdenBitcoinWalletProviderParams): BitcoinWalletProvider => {
  const chain: Chain = 'BTC';

  const walletProviderType = WalletProviderEnum.BrowserExtension;

  const key = formatWalletProviderKey({
    chain,
    displayName: MAGIC_EDEN_METADATA.displayName,
    walletProviderType,
  });

  const getNetworkPayload = async () => {
    const activeNetworkId = await getActiveNetworkId();
    const networkType = convertNetworkIdToSatsConnectNetworkType(
      activeNetworkId.networkId
    );

    return {
      network: { type: networkType },
    };
  };

  const connect: BitcoinWalletProvider['connect'] = async () => {
    const { network } = await getNetworkPayload();

    return new Promise((resolve, reject) => {
      void getAddress({
        getProvider: async () => satsConnectProvider,
        onCancel: () => {
          reject(new UserRejectedError({ action: 'connect' }));
        },
        onFinish: async (response: GetAddressResponse) => {
          const { addresses: connectedAddresses } = response;

          const { addresses } = parseBitcoinConnectionResult({
            connectedAddresses,
          });

          resolve({
            addresses,
          });
        },
        payload: {
          message: 'Address for receiving Ordinals and payments',
          network,
          purposes: [AddressPurpose.Ordinals, AddressPurpose.Payment],
        },
      });
    });
  };

  const getActiveNetworkId: BitcoinWalletProvider['getActiveNetworkId'] =
    async () =>
      getActiveNetworkIdFromLastKnownRegistry({
        client: dynamicClient,
        walletProviderKey: key,
      });

  // there's no specific method to get connected addresses in Magic Eden
  // so we use the connect method to get the addresses
  const getConnectedAddresses: BitcoinWalletProvider['getConnectedAddresses'] =
    async () => {
      const { addresses } = await connect();
      return { addresses: addresses.map((address) => address.address) };
    };

  const sendBitcoin: BitcoinWalletProvider['sendBitcoin'] = async ({
    transaction,
    walletAccount,
  }): Promise<BitcoinSendTransactionResponse> => {
    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    // should use payment address if available
    const senderAddress =
      getWalletAccountAddressByType({
        type: 'payment',
        walletAccount,
      }) ?? walletAccount.address;

    const { network } = await getNetworkPayload();

    return new Promise((resolve, reject) => {
      void sendBtcTransaction({
        getProvider: async () => satsConnectProvider,
        onCancel: () => {
          reject(new UserRejectedError({ action: 'sendBitcoin' }));
        },
        onFinish: (response) => {
          resolve({ transactionId: response });
        },
        payload: {
          network,
          recipients: [
            {
              address: transaction.recipientAddress,
              amountSats: BigInt(transaction.amount),
            },
          ],
          senderAddress,
        },
      });
    });
  };

  const signMessage: BitcoinWalletProvider['signMessage'] = async ({
    message,
    walletAccount,
    protocol,
    addressType,
  }) => {
    assertDefined(walletAccount, 'Wallet account not found');

    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    const { network } = await getNetworkPayload();

    let addressToSignWith = walletAccount.address;
    if (addressType) {
      addressToSignWith =
        getWalletAccountAddressByType({
          type: addressType,
          walletAccount,
        }) ?? addressToSignWith;
    }

    return new Promise((resolve, reject) => {
      void satsConnectSignMessage({
        getProvider: async () => satsConnectProvider,
        onCancel: () => {
          reject(new UserRejectedError({ action: 'signMessage' }));
        },
        onFinish: async (response: string) => {
          resolve({ signature: response });
        },
        payload: {
          address: addressToSignWith,
          message: message,
          network,
          protocol: getSatsConnectSigningProtocol(protocol),
        },
      });
    });
  };

  const signPsbt: BitcoinWalletProvider['signPsbt'] = async ({
    request,
    walletAccount,
  }) => {
    if (!request.allowedSighash.length) {
      throw new InvalidPsbtError('allowedSighash cannot be an empty array');
    }

    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    const activeNetworkId = await getActiveNetworkId();
    const network = convertNetworkIdForPsbt(activeNetworkId.networkId);
    const { network: networkPayload } = await getNetworkPayload();

    const psbtFromBase64 = Psbt.fromBase64(request.unsignedPsbtBase64, {
      network,
    });

    const inputsToSign = getPsbtInputsToSignForSatsConnect({
      isLegacySatsConnect: true,
      psbt: psbtFromBase64,
      request,
    });

    return new Promise((resolve, reject) => {
      void signTransaction({
        getProvider: async () => satsConnectProvider,
        onCancel: () => {
          reject(new UserRejectedError({ action: 'signPsbt' }));
        },
        onFinish: (response) => {
          resolve({ signedPsbt: response.psbtBase64 });
        },
        payload: {
          broadcast: false,
          inputsToSign,
          message: 'Sign Pbst',
          network: networkPayload,
          psbtBase64: request.unsignedPsbtBase64,
        },
      });
    });
  };

  const signPsbts: BitcoinWalletProvider['signPsbts'] = async ({
    requests,
    walletAccount,
  }) => {
    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    const activeNetworkId = await getActiveNetworkId();
    const network = convertNetworkIdForPsbt(activeNetworkId.networkId);
    const { network: networkPayload } = await getNetworkPayload();

    const psbtsToSign: SignMultiplePsbtPayload[] = [];

    for (const request of requests) {
      const psbtFromBase64 = Psbt.fromBase64(request.unsignedPsbtBase64, {
        network,
      });
      const inputsToSign = getPsbtInputsToSignForSatsConnect({
        isLegacySatsConnect: true,
        psbt: psbtFromBase64,
        request,
      });

      psbtsToSign.push({
        inputsToSign,
        psbtBase64: request.unsignedPsbtBase64,
      });
    }

    const signMultipleTransactionPayload: SignMultipleTransactionsPayload = {
      message: 'Sign Pbsts',
      network: networkPayload,
      psbts: psbtsToSign,
    };

    // Magic Eden has implemented their own signTransactions feature, and have requested us
    // to ensure we use it rather than the general Sats Connect API for this method
    const request = createUnsecuredToken(
      signMultipleTransactionPayload as unknown as Json
    );
    const response = await satsConnectProvider.signMultipleTransactions(
      request
    );

    return {
      signedPsbts: response.map((response) => response.psbtBase64),
    };
  };

  const { getEventEmitter, cleanupEventEmitter } =
    createWalletProviderEventEmitter<{
      removeAccountChangeListener: VoidFunction;
    }>({
      removeEventListeners: ({ setupReturnValue }) => {
        assertDefined(setupReturnValue, 'Setup return value not defined');

        setupReturnValue.removeAccountChangeListener();
      },

      setupEventListeners: ({ handleAccountsChanged }) => {
        const removeAccountChangeListener = addListener(
          'accountsChanged',
          (accounts: { address: string; purpose: AddressPurpose }[]) => {
            if (!accounts) {
              return;
            }

            handleAccountsChanged({
              addresses: accounts.map((account) => account.address),
            });
          }
        );

        return {
          removeAccountChangeListener,
        };
      },

      supportedEvents: ['accountsChanged'],
    });

  const terminate: BitcoinWalletProvider['terminate'] = async () => {
    cleanupEventEmitter();
  };

  return {
    chain,
    connect,
    get events() {
      return getEventEmitter();
    },
    executeCheckoutTransaction: (args) =>
      bitcoinExecuteCheckoutTransaction(args, dynamicClient),
    getActiveNetworkId,
    getConnectedAddresses,
    groupKey: formatWalletProviderGroupKey(MAGIC_EDEN_METADATA.displayName),
    key,
    metadata: MAGIC_EDEN_METADATA,
    sendBitcoin,
    signMessage,
    signPsbt,
    signPsbts,
    terminate,
    transferAmount: (args) => bitcoinTransferAmount(args, dynamicClient),
    walletProviderType,
  };
};
